<template>
  <div v-if="currentOrder !== undefined" id="content-page-table" class="table-fix-head" :style="fixHeadCalculatedStyle">
    <table class="table table-sm table-fit align-middle">
      <thead>
        <!-- Show on large screen -->
        <tr class="d-none d-sm-table-row">
          <th class="mel-bar"></th>
          <th>
            {{ $t('field.generic.item') }}
            <font-awesome-icon :icon="['fas', 'sort-up']" />
          </th>
          <th>{{ $t('field.generic.quantity') }}</th>
          <th v-if="showDeliveredQuantity">{{ $t('field.receiving.deliveredQuantity') }}</th>
          <th v-if="showDeliveredQuantity">{{ $t('field.masterdata.lot') }}</th>
          <th v-if="showDeliveredQuantity && hasBestBeforeOnAnyLine">{{ $t('field.masterdata.bestBefore') }}</th>
          <th v-if="showDeliveredQuantity && hasExpiryOnAnyLine">{{ $t('field.masterdata.expiry') }}</th>
        </tr>
        <!-- Show on small screen -->
        <tr class="d-table-row d-sm-none">
          <th class="mel-bar"></th>
          <th></th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(entry, index) in uiList" :key="index">
          <!-- Show on large screen -->
          <tr class="d-none d-sm-table-row" :class="{ 'is-filled-danger': entry.isMissing, 'is-filled-warning': entry.isUnderdelivered, 'is-filled-info': entry.isOverdelivered, 'is-filled-success-NONE': entry.isExactDelivered }">
            <td class="mel-bar" :class="{ 'mel-bar-danger': entry.isMissing, 'mel-bar-warning': entry.isUnderdelivered, 'mel-bar-info': entry.isOverdelivered, 'mel-bar-success-NONE': entry.isExactDelivered }"></td>
            <td><ItemField :item="entry.item" :substitute-item-code="entry.supplierItemCode" /></td>
            <td><QuantityField :quantity="entry.quantity" :unit-name="entry.unitName" :pad-length="maxQuantityLength" /></td>
            <td v-if="showDeliveredQuantity"><QuantityField :quantity="entry.deliveredQuantity ?? 0" :unit-name="entry.unitName" :pad-length="maxQuantityLength" /></td>
            <td v-if="showDeliveredQuantity"><LotField v-if="entry.trackStock && entry.item.trackLotNumber" :item="entry.item" :lot="entry.deliveredLot" :warn-empty="true" /></td>
            <td v-if="showDeliveredQuantity && hasBestBeforeOnAnyLine">
              <span v-if="entry.trackStock && entry.item.trackBestBeforeDate && entry.deliveredBestBefore">{{ $d(new Date(entry.deliveredBestBefore), 'dateonly') }}</span>
              <span v-else-if="entry.trackStock && entry.item.trackBestBeforeDate && !entry.deliveredBestBefore" class="text-warning"><font-awesome-icon :icon="['fas', 'square-dashed']" /></span>
            </td>
            <td v-if="showDeliveredQuantity && hasExpiryOnAnyLine">
              <span v-if="entry.trackStock && entry.item.trackExpirationDate && entry.deliveredExpiry">{{ $d(new Date(entry.deliveredExpiry), 'dateonly') }}</span>
              <span v-else-if="entry.trackStock && entry.item.trackExpirationDate && !entry.deliveredExpiry" class="text-warning"><font-awesome-icon :icon="['fas', 'square-dashed']" /></span>
            </td>
          </tr>
          <!-- Show on small screen (row 1/2) -->
          <tr class="d-table-row d-sm-none border-bottom-0" :class="{ 'is-filled-danger': entry.isMissing, 'is-filled-warning': entry.isUnderdelivered, 'is-filled-info': entry.isOverdelivered, 'is-filled-success-NONE': entry.isExactDelivered }">
            <td class="mel-bar" :class="{ 'mel-bar-danger': entry.isMissing, 'mel-bar-warning': entry.isUnderdelivered, 'mel-bar-info': entry.isOverdelivered, 'mel-bar-success-NONE': entry.isExactDelivered }"></td>
            <td><ItemField :item="entry.item" :substitute-item-code="entry.supplierItemCode" /></td>
            <td><QuantityField :quantity="entry.quantity" :unit-name="entry.unitName" :pad-length="maxQuantityLength" /></td>
          </tr>
          <!-- Show on small screen (row 2/2) -->
          <tr class="d-table-row d-sm-none" :class="{ 'is-filled-danger': entry.isMissing, 'is-filled-warning': entry.isUnderdelivered, 'is-filled-info': entry.isOverdelivered, 'is-filled-success-NONE': entry.isExactDelivered }">
            <td class="mel-bar" :class="{ 'mel-bar-danger': entry.isMissing, 'mel-bar-warning': entry.isUnderdelivered, 'mel-bar-info': entry.isOverdelivered, 'mel-bar-success-NONE': entry.isExactDelivered }"></td>
            <td colspan="2">
              <div class="d-flex gap-2 align-items-center justify-content-between">
                <LotField v-if="entry.trackStock && entry.item.trackLotNumber" :item="entry.item" :lot="entry.deliveredLot" :show-icon="true" />
                <span v-if="entry.trackStock && entry.item.trackBestBeforeDate && entry.deliveredBestBefore">{{ $d(new Date(entry.deliveredBestBefore), 'dateonly') }}</span>
                <span v-if="entry.trackStock && entry.item.trackExpirationDate && entry.deliveredExpiry">{{ $d(new Date(entry.deliveredExpiry), 'dateonly') }}</span>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
  <div v-else>
    <slot name="empty-list" />
  </div>

</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useOrdersStore } from '@/stores';
import { ItemField, QuantityField, LotField } from '@/components/fields';

const ordersStore = useOrdersStore();

const props = defineProps({
  orderId: { type: Number, required: true },
  showDeliveredQuantity: { type: Boolean, default: false }
});

const offsetTop = ref(0);

onMounted(() => {
  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const uiList = computed(() => {
  if (currentOrder.value === undefined) {
    return [];
  }
  return [...currentOrder.value.lines].sort((a, b) =>
    a.item.name.localeCompare(b.item.name)
  );
});

const hasBestBeforeOnAnyLine = computed(() => {
  return uiList.value !== undefined && uiList.value.findIndex((x) => !!x.item.trackBestBeforeDate) !== -1;
});

const hasExpiryOnAnyLine = computed(() => {
  return uiList.value !== undefined && uiList.value.findIndex((x) => !!x.item.trackExpirationDate) !== -1;
});

const maxQuantityLength = computed(() => {
  return uiList.value.reduce((acc, item) => Math.max(acc, item.quantity.toString().length), 0);
});

const currentOrder = computed(() => {
  return ordersStore.getById(props.orderId)
});
</script>

<style scoped type="scss">
tr.is-filled-primary > td {
  background-color: var(--bs-primary-bg-subtle);
}
tr.is-filled-secondary > td {
  background-color: var(--bs-secondary-bg-subtle);
}
tr.is-filled-success > td {
  background-color: var(--bs-success-bg-subtle);
}
tr.is-filled-warning > td {
  background-color: var(--bs-warning-bg-subtle);
}
tr.is-filled-danger > td {
  background-color: var(--bs-danger-bg-subtle);
}
tr.is-filled-info > td {
  background-color: var(--bs-info-bg-subtle);
}

table td.mel-bar,
table th.mel-bar
{
  padding-left: 0;
  padding-right: 0;
  width: 6px;
  min-width: 6px;
  max-width: 6px;
}

.table > :not(caption) > * > .mel-bar-none,
.mel-bar-none {
  background-color: transparent;
}
.table > :not(caption) > * > .mel-bar-secondary,
.mel-bar-secondary {
  background-color: var(--bs-body-bg);
}
.table > :not(caption) > * > .mel-bar-primary,
.mel-bar-primary {
  background-color: var(--bs-primary);
}
.table > :not(caption) > * > .mel-bar-success,
.mel-bar-success {
  background-color: var(--bs-success);
}
.table > :not(caption) > * > .mel-bar-warning,
.mel-bar-warning {
  background-color: var(--bs-warning);
}
.table > :not(caption) > * > .mel-bar-danger,
.mel-bar-danger {
  background-color: var(--bs-danger);
}
.table > :not(caption) > * > .mel-bar-info,
.mel-bar-info {
  background-color: var(--bs-info);
}

.table > :not(caption) > *.border-bottom-0 > * {
  border-bottom-width: 0;
}
</style>
